import { useState, useEffect } from 'react'
import { TrendingUp, RefreshCw, DollarSign, CheckCircle, Percent, Users, Loader2, BarChart3, Wallet, ChevronDown, Info, Lock, ChevronLeft } from 'lucide-react'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useUser } from '@/shared/hooks/useUser'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useUserBotStatistics, useUserResourceStatistics } from '@/shared/hooks/useUserStatistics'
import { useResources } from '@/shared/hooks/useResources'
import { formatToMoscowTime } from '@/shared/utils/format'
import type { SourceFilter } from '@/shared/types/statistics'

type TimeRange = '24h' | '7d' | '30d'
type TabType = 'bots' | 'resources'
type MetricType = 'traffic' | 'revenue'

export default function Statistics() {
  const { telegramId, hapticFeedback, hapticNotification } = useTelegram()
  const { data: user, isLoading: userLoading } = useUser(telegramId)
  const { data: resources } = useResources(user?.id)

  const [activeTab, setActiveTab] = useState<TabType>('resources')
  const [timeRange, setTimeRange] = useState<TimeRange>('24h')
  const [metricType, setMetricType] = useState<MetricType>('traffic')
  const [forceRefresh, setForceRefresh] = useState(false)
  const [isTimeRangeChanging, setIsTimeRangeChanging] = useState(false)
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const [selectedSource, setSelectedSource] = useState<SourceFilter>('all')
  const [isRetentionExpanded, setIsRetentionExpanded] = useState(false)

  // Calculate date range based on selected time range
  const getDateRange = (range: TimeRange): { start: string; end: string; granularity: 'hour' | 'day' } => {
    const end = new Date()
    const start = new Date()

    switch (range) {
      case '24h':
        start.setDate(start.getDate() - 1)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'hour',
        }
      case '7d':
        start.setDate(start.getDate() - 7)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'day',
        }
      case '30d':
        start.setDate(start.getDate() - 30)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'day',
        }
    }
  }

  const dateRange = getDateRange(timeRange)

  // Fetch bot statistics
  const {
    data: botStats,
    isLoading: botLoading,
    error: botError,
    refetch: refetchBots,
    isFetching: botFetching
  } = useUserBotStatistics(
    user?.id,
    dateRange.start,
    dateRange.end,
    dateRange.granularity,
    { enabled: activeTab === 'bots', refresh: forceRefresh }
  )

  // Fetch resource statistics
  const {
    data: resourceStats,
    isLoading: resourceLoading,
    error: resourceError,
    refetch: refetchResources,
    isFetching: resourceFetching
  } = useUserResourceStatistics(
    user?.id,
    dateRange.start,
    dateRange.end,
    dateRange.granularity,
    { enabled: activeTab === 'resources', refresh: forceRefresh }
  )

  const isLoading = activeTab === 'bots' ? botLoading : resourceLoading
  const isFetching = activeTab === 'bots' ? botFetching : resourceFetching
  const error = activeTab === 'bots' ? botError : resourceError

  // Extract filtered data based on selected source
  const currentBotSourceData = botStats?.all_sources?.[selectedSource]
  const currentResourceSourceData = resourceStats?.all_sources?.[selectedSource]

  const stats = activeTab === 'bots' ? currentBotSourceData : currentResourceSourceData

  useEffect(() => {
    window.scrollTo(0, 0)
    document.body.style.backgroundColor = '#1e2832'
    return () => {
      document.body.style.backgroundColor = ''
    }
  }, [])

  // Handle refresh
  const handleRefresh = async () => {
    hapticFeedback('medium')
    setForceRefresh(true)
    try {
      if (activeTab === 'bots') {
        await refetchBots()
      } else {
        await refetchResources()
      }
      hapticNotification('success')
    } catch (err) {
      hapticNotification('error')
    } finally {
      setForceRefresh(false)
    }
  }

  // Handle tab change
  const handleTabChange = (tab: TabType) => {
    hapticFeedback('light')
    setActiveTab(tab)
  }

  // Handle time range change
  const handleTimeRangeChange = (range: TimeRange) => {
    hapticFeedback('light')
    setIsTimeRangeChanging(true)
    setTimeRange(range)
  }

  useEffect(() => {
    if (!isFetching && isTimeRangeChanging) {
      setIsTimeRangeChanging(false)
    }
  }, [isFetching, isTimeRangeChanging])

  // Handle metric type change
  const handleMetricTypeChange = (type: MetricType) => {
    hapticFeedback('light')
    setMetricType(type)
  }

  // Toggle info section
  const toggleInfo = () => {
    hapticFeedback('light')
    setIsInfoOpen(!isInfoOpen)
  }

  // Handle source selection
  const handleSourceSelect = (source: SourceFilter) => {
    hapticFeedback('light')
    setSelectedSource(source)
    setIsInfoOpen(false) // Close dropdown after selection
  }

  // Transform data for charts
  const getChartData = () => {
    if (!stats?.time_series || stats.time_series.length === 0) return []

    if (activeTab === 'bots') {
      return currentBotSourceData?.time_series.map((item) => {
        const timeLabel = formatToMoscowTime(item.time_key, dateRange.granularity)

        return {
          time: timeLabel,
          fullTime: item.time_key,
          revenue: item.revenue,
          users: item.unique_users,
          tasksCreated: item.tasks_created,
          tasksCompleted: item.tasks_completed,
        }
      }) || []
    } else {
      return currentResourceSourceData?.time_series.map((item) => {
        const timeLabel = formatToMoscowTime(item.time_key, dateRange.granularity)

        return {
          time: timeLabel,
          fullTime: item.time_key,
          spend: item.spend,
          channelSubscribers: item.channel_subscribers,
          channelUnsubscribers: item.channel_unsubscribers,
          boostCount: item.boost_count,
          unboostCount: item.unboost_count,
          botActivations: item.bot_activations,
          botBlocks: item.bot_blocks,
        }
      }) || []
    }
  }

  if (userLoading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const timeRanges = [
    { value: '24h' as const, label: '24ч', icon: '⏰' },
    { value: '7d' as const, label: '7д', icon: '📅' },
    { value: '30d' as const, label: '30д', icon: '📅' },
  ]

  const chartData = getChartData()
  const hasChartData = chartData.length > 0

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#3b82f6]/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#3b82f6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Общая статистика</h1>
              <p className="text-sm text-tg-hint mt-1">Агрегированные данные</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 py-4 space-y-3">
        {/* Tab Selector */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex gap-1 p-1 bg-tg-bg rounded-xl">
            <button
              onClick={() => handleTabChange('resources')}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'resources'
                  ? 'bg-tg-button text-white shadow-lg'
                  : 'text-tg-hint hover:text-tg-text'
              }`}
            >
              <span className="text-base mr-1">📢</span>
              Мои ресурсы
            </button>
            <button
              onClick={() => handleTabChange('bots')}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'bots'
                  ? 'bg-tg-button text-white shadow-lg'
                  : 'text-tg-hint hover:text-tg-text'
              }`}
            >
              <span className="text-base mr-1">🤖</span>
              Мои боты
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex gap-1 p-1 bg-tg-bg rounded-xl">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => handleTimeRangeChange(range.value)}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  timeRange === range.value
                    ? 'bg-tg-button text-white shadow-lg'
                    : 'text-tg-hint hover:text-tg-text'
                }`}
              >
                <span className="text-base mr-1">{range.icon}</span>
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Refresh Section */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <p className="text-xs text-tg-hint">Последнее обновление</p>
              <p className="text-sm font-medium text-tg-text mt-0.5">Только что</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isFetching}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                isFetching
                  ? 'bg-tg-hint/20 text-tg-hint cursor-not-allowed'
                  : 'bg-tg-button text-white hover:shadow-lg active:scale-95'
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              {isFetching ? 'Загрузка...' : 'Обновить'}
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">❌</div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Не удалось загрузить статистику</h3>
            <p className="text-sm text-red-600 mb-4">{error.message}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Повторить
            </button>
          </div>
        )}

        {/* Loading Skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-tg-secondary-bg rounded-2xl p-4 h-32 animate-pulse" />
            ))}
          </div>
        )}

        {/* Summary Cards - Bot Owner */}
        {activeTab === 'bots' && currentBotSourceData?.summary && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#059669]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#059669]/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#059669]" />
                </div>
                <span className="text-xs text-tg-hint">Доход</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {currentBotSourceData.summary.total_revenue.toFixed(2)} ₽
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                {currentBotSourceData.summary.avg_daily_revenue.toFixed(2)} ₽/день
              </div>
            </div>

            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#3b82f6]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#3b82f6]" />
                </div>
                <span className="text-xs text-tg-hint">Выполнено</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {currentBotSourceData.summary.total_completed}
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                из {currentBotSourceData.summary.total_tasks} задач
              </div>
            </div>

            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#f59e0b]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#f59e0b]/10 rounded-lg flex items-center justify-center">
                  <Percent className="w-5 h-5 text-[#f59e0b]" />
                </div>
                <span className="text-xs text-tg-hint">Конверсия</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {(currentBotSourceData.summary.avg_completion_rate * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                среднее выполнение
              </div>
            </div>

            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#8b5cf6]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#8b5cf6]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#8b5cf6]" />
                </div>
                <span className="text-xs text-tg-hint">Пользователи</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {currentBotSourceData.summary.total_unique_users}
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                {currentBotSourceData.summary.total_bots} ботов
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards - Resource Owner */}
        {activeTab === 'resources' && currentResourceSourceData?.summary && (
          <div className="space-y-3">
            {(() => {
              // Calculate totals from time series
              const totalSubscriptions = currentResourceSourceData.time_series?.reduce((sum, item) => sum + item.channel_subscribers, 0) || 0
              const totalUnsubscriptions = currentResourceSourceData.time_series?.reduce((sum, item) => sum + item.channel_unsubscribers, 0) || 0
              const totalBoosts = currentResourceSourceData.time_series?.reduce((sum, item) => sum + item.boost_count, 0) || 0
              const totalUnboosts = currentResourceSourceData.time_series?.reduce((sum, item) => sum + item.unboost_count, 0) || 0
              const totalBotActivations = currentResourceSourceData.time_series?.reduce((sum, item) => sum + item.bot_activations, 0) || 0
              const totalBotBlocks = currentResourceSourceData.time_series?.reduce((sum, item) => sum + item.bot_blocks, 0) || 0

              const hasSubscriptions = totalSubscriptions > 0 || totalUnsubscriptions > 0
              const hasBoosts = totalBoosts > 0 || totalUnboosts > 0
              const hasBotActivations = totalBotActivations > 0 || totalBotBlocks > 0

              const cards = []

              // Always add spend card first
              cards.push(
                <div key="spend" className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#ef4444]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-[#ef4444]/10 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#ef4444]" />
                    </div>
                    <span className="text-xs text-tg-hint">Потрачено</span>
                  </div>
                  <div className="text-xl font-bold text-tg-text">
                    {currentResourceSourceData.summary.total_spend.toFixed(2)} ₽
                  </div>
                  <div className="text-xs text-tg-hint mt-0.5">
                    {currentResourceSourceData.summary.avg_daily_spend.toFixed(2)} ₽/день
                  </div>
                </div>
              )

              // Add task type cards
              if (hasSubscriptions) {
                cards.push(
                  <div key="subscriptions" className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#3b82f6]/20">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-[#3b82f6]" />
                      </div>
                      <span className="text-xs text-tg-hint">Подписки</span>
                    </div>
                    <div className="text-xl font-bold text-tg-text">
                      {totalSubscriptions}
                    </div>
                    <div className="text-xs text-tg-hint mt-0.5">
                      {currentResourceSourceData.summary.pending_verification} в ожидании
                    </div>
                    <div className="text-xs text-tg-hint">
                      {totalUnsubscriptions} отписок
                    </div>
                  </div>
                )
              }

              if (hasBoosts) {
                cards.push(
                  <div key="boosts" className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#eab308]/20">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-[#eab308]/10 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-[#eab308]" />
                      </div>
                      <span className="text-xs text-tg-hint">Бусты</span>
                    </div>
                    <div className="text-xl font-bold text-tg-text">
                      {totalBoosts}
                    </div>
                    <div className="text-xs text-tg-hint mt-0.5">
                      {currentResourceSourceData.summary.pending_verification} в ожидании
                    </div>
                    <div className="text-xs text-tg-hint">
                      {totalUnboosts} отменено
                    </div>
                  </div>
                )
              }

              if (hasBotActivations) {
                cards.push(
                  <div key="activations" className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#8b5cf6]/20">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-[#8b5cf6]/10 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-[#8b5cf6]" />
                      </div>
                      <span className="text-xs text-tg-hint">Активации ботов</span>
                    </div>
                    <div className="text-xl font-bold text-tg-text">
                      {totalBotActivations}
                    </div>
                    <div className="text-xs text-tg-hint mt-0.5">
                      {currentResourceSourceData.summary.pending_verification} в ожидании
                    </div>
                    <div className="text-xs text-tg-hint">
                      {totalBotBlocks} блокировок
                    </div>
                  </div>
                )
              }

              return <div className="grid grid-cols-2 gap-3">{cards}</div>
            })()}

            {/* Retention Card */}
            <button
              onClick={() => {
                hapticFeedback('light')
                setIsRetentionExpanded(!isRetentionExpanded)
              }}
              className="w-full bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-orange-500/20 text-left hover:bg-tg-hint/5 active:bg-tg-hint/10 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <Lock className="w-5 h-5 text-orange-500" />
                  </div>
                  <span className="text-xs text-tg-hint">Удержание</span>
                </div>
                <ChevronLeft
                  className={`w-5 h-5 text-orange-500 transition-transform duration-200 ${
                    isRetentionExpanded ? '-rotate-90' : 'rotate-180'
                  }`}
                />
              </div>
              <div className="text-xl font-bold text-tg-text">
                {(() => {
                  const totalHoldingFees = resources?.reduce((sum, resource) => sum + (resource.holding_fee_amount || 0), 0) || 0
                  const resourceRetention = user?.resource_retention || 0
                  return (totalHoldingFees + resourceRetention).toFixed(2)
                })()} USDT
              </div>

              {isRetentionExpanded && (
                <>
                  <div className="mt-2 space-y-1">
                    <div className="text-xs">
                      <span className="text-tg-hint">Холдинг ресурсов:</span>
                      <div className="text-tg-text font-medium">
                        {(resources?.reduce((sum, resource) => sum + (resource.holding_fee_amount || 0), 0) || 0).toFixed(2)} USDT
                      </div>
                    </div>
                    <div className="text-xs">
                      <span className="text-tg-hint">На проверке задач:</span>
                      <div className="text-tg-text font-medium">{(user?.resource_retention || 0).toFixed(2)} USDT</div>
                    </div>
                  </div>
                  <p className="text-xs text-tg-hint mt-2">
                    Холдинг удержан на всех активных ресурсах
                  </p>
                </>
              )}
            </button>
          </div>
        )}

        {/* Data Source Filter - Collapsible (Only for bots) */}
        {activeTab === 'bots' && (
          <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
            {/* Toggle Button */}
            <button
              onClick={toggleInfo}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-tg-bg/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-tg-hint" />
                <span className="text-sm font-medium text-tg-text">Источник данных</span>
                <span className="text-base flex h-5 w-5 items-center justify-center">
                  {selectedSource === 'tgrass' ? (
                    <img src="/images/integrations/tgrass.svg" alt="" className="h-5 w-5 rounded object-cover" />
                  ) : ({
                      all: '📊',
                      internal: '🏠',
                      flyer: '✈️',
                      subgram: '📱',
                      fdu: 'F',
                      tgrass: 'T'
                    }[selectedSource])}
                </span>
                <span className="text-xs text-tg-hint">
                  {{
                    all: 'Все источники',
                    internal: 'Внутренние',
                    flyer: 'Flyer',
                    subgram: 'Subgram',
                    fdu: 'FDU',
                    tgrass: 'Tgrass'
                  }[selectedSource]}
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-tg-hint transition-transform duration-200 ${isInfoOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Collapsible Source Buttons */}
            {isInfoOpen && (
              <div className="px-3 pb-3 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'all' as const, label: 'Все', icon: '📊', color: 'from-blue-500/20 to-blue-600/20 border-blue-500/30' },
                    { value: 'internal' as const, label: 'Внутренние', icon: '🏠', color: 'from-green-500/20 to-green-600/20 border-green-500/30' },
                    { value: 'flyer' as const, label: 'Flyer', icon: '✈️', color: 'from-purple-500/20 to-purple-600/20 border-purple-500/30' },
                    { value: 'subgram' as const, label: 'Subgram', icon: '📱', color: 'from-orange-500/20 to-orange-600/20 border-orange-500/30' },
                    { value: 'fdu' as const, label: 'FDU', icon: 'F', color: 'from-red-500/20 to-red-600/20 border-red-500/30' },
                    { value: 'tgrass' as const, label: 'Tgrass', icon: 'T', color: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30' },
                  ].map((source) => (
                    <button
                      key={source.value}
                      onClick={() => handleSourceSelect(source.value)}
                      className={`py-3 px-3 rounded-xl font-medium text-sm transition-all flex flex-col items-center justify-center gap-1 border active:scale-95 ${
                        selectedSource === source.value
                          ? `bg-gradient-to-br ${source.color} shadow-lg scale-[1.02]`
                          : 'bg-tg-bg border-transparent text-tg-hint hover:text-tg-text hover:border-tg-hint/20'
                      }`}
                    >
                    {source.value === 'tgrass' ? (
                      <img src="/images/integrations/tgrass.svg" alt="" className="h-7 w-7 rounded-md object-cover" />
                    ) : (
                      <span className="text-2xl">{source.icon}</span>
                    )}
                    <span className="text-xs font-semibold">{source.label}</span>
                  </button>
                  ))}
                </div>
                <p className="text-[10px] text-tg-hint text-center mt-3 px-2">
                  ℹ️ Агрегированная статистика показывает данные из всех источников
                </p>
              </div>
            )}
          </div>
        )}

        {/* Chart Section */}
        {stats && hasChartData && (
          <>
            {/* Metric Type Toggle */}
            <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
              <div className="flex gap-2">
                <button
                  onClick={() => handleMetricTypeChange('traffic')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    metricType === 'traffic'
                      ? 'bg-[#3b82f6] text-white shadow-lg'
                      : 'bg-tg-bg text-tg-hint hover:text-tg-text'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Трафик
                </button>
                <button
                  onClick={() => handleMetricTypeChange('revenue')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    metricType === 'revenue'
                      ? 'bg-[#059669] text-white shadow-lg'
                      : 'bg-tg-bg text-tg-hint hover:text-tg-text'
                  }`}
                >
                  <Wallet className="w-4 h-4" />
                  {activeTab === 'bots' ? 'Доход' : 'Расходы'}
                </button>
              </div>
            </div>

            {/* Chart Container */}
            {isTimeRangeChanging ? (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
                <div className="h-[440px] animate-pulse flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
                </div>
              </div>
            ) : (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
                <h3 className="text-sm font-semibold text-tg-text mb-2 px-1">
                  {metricType === 'traffic' ? 'График трафика' : activeTab === 'bots' ? 'График дохода' : 'График расходов'}
                </h3>

                <div className="w-full h-[400px] -ml-2">
                  <ResponsiveContainer width="100%" height="100%" key={`${timeRange}-${metricType}-${activeTab}`}>
                    {metricType === 'traffic' ? (
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                        <XAxis dataKey="time" stroke="#6b7280" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <YAxis stroke="#6b7280" tick={{ fill: '#9ca3af', fontSize: 11 }} width={40} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1e2832',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#f9fafb',
                          }}
                          labelStyle={{ color: '#9ca3af' }}
                        />
                        <Legend wrapperStyle={{ color: '#9ca3af' }} iconType="line" />
                        {activeTab === 'bots' ? (
                          <>
                            <Line type="monotone" dataKey="tasksCreated" stroke="#3b82f6" strokeWidth={2} name="Всего задач" dot={false} />
                            <Line type="monotone" dataKey="tasksCompleted" stroke="#059669" strokeWidth={2} name="Выполнено" dot={false} />
                            <Line type="monotone" dataKey="users" stroke="#8b5cf6" strokeWidth={2} name="Пользователи" dot={false} />
                          </>
                        ) : (
                          <>
                            {chartData.some((d: any) => d.channelSubscribers > 0) && (
                              <Line type="monotone" dataKey="channelSubscribers" stroke="#059669" strokeWidth={2} name="Подписки" dot={false} />
                            )}
                            {chartData.some((d: any) => d.channelUnsubscribers > 0) && (
                              <Line type="monotone" dataKey="channelUnsubscribers" stroke="#ef4444" strokeWidth={2} name="Отписки" dot={false} />
                            )}
                            {chartData.some((d: any) => d.boostCount > 0) && (
                              <Line type="monotone" dataKey="boostCount" stroke="#eab308" strokeWidth={2} name="Бусты" dot={false} />
                            )}
                            {chartData.some((d: any) => d.unboostCount > 0) && (
                              <Line type="monotone" dataKey="unboostCount" stroke="#f97316" strokeWidth={2} name="Разбусты" dot={false} />
                            )}
                            {chartData.some((d: any) => d.botActivations > 0) && (
                              <Line type="monotone" dataKey="botActivations" stroke="#3b82f6" strokeWidth={2} name="Активации ботов" dot={false} />
                            )}
                            {chartData.some((d: any) => d.botBlocks > 0) && (
                              <Line type="monotone" dataKey="botBlocks" stroke="#8b5cf6" strokeWidth={2} name="Блокировки ботов" dot={false} />
                            )}
                          </>
                        )}
                      </LineChart>
                    ) : (
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={activeTab === 'bots' ? '#059669' : '#ef4444'} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={activeTab === 'bots' ? '#059669' : '#ef4444'} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                        <XAxis dataKey="time" stroke="#6b7280" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <YAxis stroke="#6b7280" tick={{ fill: '#9ca3af', fontSize: 11 }} width={40} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1e2832',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#f9fafb',
                          }}
                          labelStyle={{ color: '#9ca3af' }}
                          formatter={(value: number) => `${value.toFixed(2)} ₽`}
                        />
                        <Legend wrapperStyle={{ color: '#9ca3af' }} />
                        <Area
                          type="monotone"
                          dataKey={activeTab === 'bots' ? 'revenue' : 'spend'}
                          stroke={activeTab === 'bots' ? '#059669' : '#ef4444'}
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#revenueGradient)"
                          name={activeTab === 'bots' ? 'Доход (₽)' : 'Расходы (₽)'}
                        />
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}

        {/* Breakdown Table - Bot Owner */}
        {activeTab === 'bots' && currentBotSourceData?.bot_breakdown && currentBotSourceData.bot_breakdown.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-tg-text px-1">Детализация по ботам</h3>
            {currentBotSourceData.bot_breakdown.map((bot) => (
              <div key={bot.bot_id} className="bg-tg-secondary-bg rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-base font-medium text-tg-text">{bot.bot_name}</div>
                  <div className="text-xl font-bold text-tg-text whitespace-nowrap ml-2">{bot.revenue.toFixed(2)} ₽</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-tg-hint mb-1">Активные</div>
                    <div className="text-[#059669] font-semibold text-base">{bot.unique_users}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-tg-hint mb-1">Отменено</div>
                    <div className="text-[#ef4444] font-semibold text-base">{bot.tasks_created - bot.tasks_completed}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Breakdown Table - Resource Owner */}
        {activeTab === 'resources' && currentResourceSourceData?.resource_breakdown && currentResourceSourceData.resource_breakdown.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-tg-text px-1">Детализация по ресурсам</h3>
            {currentResourceSourceData.resource_breakdown.map((resource) => (
              <div key={resource.resource_id} className="bg-tg-secondary-bg rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-base font-medium text-tg-text">{resource.resource_name}</div>
                  <div className="text-xl font-bold text-tg-text whitespace-nowrap ml-2">{resource.spend.toFixed(2)} ₽</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-tg-hint mb-1">Активные</div>
                    <div className="text-[#059669] font-semibold text-base">{resource.active_subscribers}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-tg-hint mb-1">Отменено</div>
                    <div className="text-[#ef4444] font-semibold text-base">{resource.cancelled_tasks}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!stats && !isLoading && !error && (
          <div className="bg-tg-secondary-bg rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-tg-text mb-2">Нет данных</h3>
            <p className="text-sm text-tg-hint mb-4">
              {activeTab === 'bots'
                ? 'У вас еще нет ботов. Добавьте бот, чтобы увидеть статистику.'
                : 'У вас еще нет ресурсов. Добавьте ресурс, чтобы увидеть статистику.'}
            </p>
          </div>
        )}

        {/* No Data State for Chart */}
        {stats && !hasChartData && (
          <div className="bg-tg-secondary-bg rounded-2xl p-8 text-center">
            <div className="text-4xl mb-3">📉</div>
            <h3 className="text-lg font-semibold text-tg-text mb-2">Нет данных</h3>
            <p className="text-sm text-tg-hint">
              За выбранный период нет данных для отображения
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
